import { CUSTOM_FILTERS } from "@/config/list";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";

const FILTERS_STRING = [
	CUSTOM_FILTERS.networkType?.toString(),
	CUSTOM_FILTERS.search?.toString(),
	CUSTOM_FILTERS.status?.toString(),
	CUSTOM_FILTERS.timePeriod?.toString(),
	CUSTOM_FILTERS.structureGroupIds.toString(),
	CUSTOM_FILTERS.structureTypesIds.toString(),
	CUSTOM_FILTERS.from?.toString(),
	CUSTOM_FILTERS.to?.toString(),
	CUSTOM_FILTERS.alarmType?.toString(),
	CUSTOM_FILTERS.perspective?.toString(),
];

// Boolean filters round-trip as "true"/"false" and are read back as a plain
// boolean (not wrapped in an array), so call sites can read `f.value as boolean`.
const FILTERS_BOOLEAN = [
	CUSTOM_FILTERS.enabled.toString(),
	CUSTOM_FILTERS.ownAlarms.toString(),
	CUSTOM_FILTERS.isMostDownstream.toString(),
	CUSTOM_FILTERS.hasData.toString(),
	CUSTOM_FILTERS.isFullyConfiguredForIAndI.toString(),
];

interface Options {
	enableUrlParams?: boolean;
	initFromUrl?: boolean;
}

interface QueryParams {
	page: number;
	pageSize: number;
	sort?: { id: string; desc: boolean }[];
	filters?: { id: string; value: any }[];
	globalFilter?: string;
}

const parseInitialParamsFromUrl = (searchParams: URLSearchParams): Partial<QueryParams> => {
	const page = Number(searchParams.get("page")) || 0;
	const pageSize = Number(searchParams.get("pageSize")) || 10;

	const filters: QueryParams["filters"] = [];
	const sort: QueryParams["sort"] = [];

	searchParams.forEach((value, key) => {
		const filterMatch = key.match(/^filter\[(\d+)]\.(id|value)$/);
		if (filterMatch) {
			const [_, indexStr, prop] = filterMatch;
			const index = Number(indexStr);

			filters[index] = filters[index] || { id: "", value: "" };
			if (prop === "id") filters[index].id = value;
			if (prop === "value") {
				const id = filters[index].id;
				const isStringFilter = FILTERS_STRING.includes(id);
				const isBooleanFilter = FILTERS_BOOLEAN.includes(id);
				const isSingleStringFilter =
					CUSTOM_FILTERS.search.toString() === id || CUSTOM_FILTERS.isActive.toString() === id;
				if (isSingleStringFilter) {
					filters[index].value = value;
				} else if (isBooleanFilter) {
					filters[index].value = value === "true";
				} else {
					filters[index].value = value
						.split(",")
						.map(v => v.trim())
						.map(v => (isStringFilter ? v : Number(v)));
				}
			}
		}

		const sortMatch = key.match(/^sort\[(\d+)]\.(id|desc)$/);
		if (sortMatch) {
			const [, indexStr, prop] = sortMatch;
			const index = Number(indexStr);
			sort[index] = sort[index] || { id: "", desc: false };
			if (prop === "id") sort[index].id = value;
			if (prop === "desc") {
				// accept "true"/"false" and also "-1"/"1" if they appear
				if (value === "true") sort[index].desc = true;
				else if (value === "false") sort[index].desc = false;
				else if (value === "-1") sort[index].desc = true;
				else if (value === "1") sort[index].desc = false;
			}
			return;
		}
	});
	return { page, pageSize, filters, sort };
};

interface QueryState {
	page?: number;
	pageSize?: number;
	filters?: {
		id: string;
		value: any;
	}[];
	sort?: {
		id: string;
		desc: boolean;
	}[];
}

interface StateSetters {
	setPage?: (page: number) => void;
	setPageSize?: (pageSize: number) => void;
	setFilters?: (
		filters: {
			id: string;
			value: any;
		}[],
	) => void;
	setSorting?: (
		sort: {
			id: string;
			desc: boolean;
		}[],
	) => void;
}

export const useUrlQuerySync = (state: QueryState, setter: StateSetters, options?: Options): QueryParams => {
	const [searchParams, setSearchParams] = useSearchParams();
	// Initialize from URL once
	useEffect(() => {
		if (!options?.initFromUrl) return;
		const parsed = parseInitialParamsFromUrl(searchParams);

		if (parsed.page !== undefined) setter.setPage ? setter.setPage(parsed.page) : null;
		if (parsed.pageSize !== undefined) setter.setPageSize ? setter.setPageSize(parsed.pageSize) : null;
		if (parsed.filters && parsed.filters.length > 0) setter.setFilters ? setter.setFilters(parsed.filters) : null;
		if (parsed.sort && parsed.sort.length > 0) setter.setSorting ? setter.setSorting(parsed.sort) : null;
	}, []);
	// Reactive query object
	//@ts-ignore
	const params: QueryParams = useMemo(
		() => ({
			page: state.page,
			pageSize: state.pageSize,
			filters: state.filters,
			sort: state.sort,
		}),
		[state.page, state.pageSize, state.sort, state.filters],
	);

	// Sync to URL
	useEffect(() => {
		if (!options?.enableUrlParams) return;

		const sp = new URLSearchParams();
		if (params.page !== undefined) sp.set("page", String(params.page));
		if (params.pageSize !== undefined) sp.set("pageSize", String(params.pageSize));

		params.sort?.forEach((sort, i) => {
			sp.set(`sort[${i}].id`, sort.id);
			sp.set(`sort[${i}].desc`, String(sort.desc));
		});
		const filters = sanitizeFilters(params.filters);

		filters.forEach((f, i) => {
			sp.set(`filter[${i}].id`, f.id);
			sp.set(`filter[${i}].value`, Array.isArray(f.value) ? f.value.join(",") : String(f.value));
		});

		setSearchParams(sp, { replace: true });
	}, [params, options?.enableUrlParams]);

	return params;
};

const isEmptyValue = (v: unknown) => {
	if (v === undefined || v === null) return true;
	if (typeof v === "string") return v.trim() === "";
	if (Array.isArray(v)) return v.length === 0 || v.every(x => String(x).trim() === "");
	return false;
};

const normalizeFilterValue = (v: unknown) => {
	if (Array.isArray(v)) {
		const cleaned = v.map(x => (typeof x === "string" ? x.trim() : x)).filter(x => String(x).trim() !== "");
		return cleaned;
	}
	if (typeof v === "string") return v.trim();
	return v;
};

const sanitizeFilters = (filters?: { id: string; value: any }[]) =>
	(filters ?? [])
		.map(f => ({ ...f, value: normalizeFilterValue(f.value) }))
		.filter(f => f.id && !isEmptyValue(f.value));
